import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useLocalizedStrings } from '../../Localization';
import { BreadcrumbProps, Button, Icon, Tabs, DataTable, Card, CardBody, Badge, Tooltip, Modal, CodeDisplay, Refreshing } from '@aidkitorg/component-library';
import { AdminPanelPage } from '../AdminPanelPage';
import { ColumnDef } from '@tanstack/react-table';
import { useNotifications, NotificationMessagePreview } from '../../Contexts/NotificationsContext';
import type { NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import { NotificationRecipientsDownloadModal } from './components/NotificationRecipientsDownloadModal';

export const NotificationRecipientsPage = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { targetFieldPrefix } = useParams<{ targetFieldPrefix: string }>();
  
  const [activeTab, setActiveTab] = useState<'sent' | 'eligible'>('sent');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState<{ kind: 'sent' | 'eligible'; recipient: NotificationRecipientsResult['sent'][number] | NotificationRecipientsResult['eligible'][number] | null; }>({ kind: 'sent', recipient: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<NotificationMessagePreview | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const {
    fetchNotificationRecipients,
    getNotificationRecipients,
    getIsRecipientsLoading,
    getRecipientsTimestamp,
    fetchRecipientPreview
  } = useNotifications();

  const recipientsData = getNotificationRecipients(targetFieldPrefix || '');
  const isLoading = getIsRecipientsLoading(targetFieldPrefix || '');
  const lastUpdatedTimestamp = getRecipientsTimestamp(targetFieldPrefix || '');

  useEffect(() => {
    if (targetFieldPrefix) {
      fetchNotificationRecipients(targetFieldPrefix);
    }
  }, [targetFieldPrefix, fetchNotificationRecipients]);

  const handleForceRefresh = () => {
    if (targetFieldPrefix) {
      fetchNotificationRecipients(targetFieldPrefix, true);
    }
  };

  // Built-in DataTable search will handle filtering
  const openPreview = async (kind: 'sent' | 'eligible', recipient: NotificationRecipientsResult['sent'][number] | NotificationRecipientsResult['eligible'][number]) => {
    setPreviewData(null);
    setPreviewLoading(true);
    setPreviewState({ kind, recipient });
    setPreviewOpen(true);
    if (!targetFieldPrefix) {
      setPreviewLoading(false);
      return;
    }
    const type = kind === 'sent' ? 'sent' : 'preview';
    const result = await fetchRecipientPreview({
      targetFieldPrefix,
      type,
      uid: recipient.uid,
      channel: recipient.channel,
      legal_name: recipient.legal_name,
    });
    setPreviewData(result);
    setPreviewLoading(false);
  }

  // Create columns with dynamic variable columns
  const createSentColumns = (): ColumnDef<NotificationRecipientsResult['sent'][number]>[] => {
    const baseColumns: ColumnDef<NotificationRecipientsResult['sent'][number]>[] = [
      {
        accessorKey: 'uid',
        header: 'UID',
        size: 120,
      },
      {
        accessorKey: 'legal_name',
        header: L.admin_panel.notifications.legal_name,
        size: 150,
      },
      {
        accessorKey: 'channel',
        header: L.admin_panel.notifications.channel,
        size: 100,
        cell: ({ row }) => {
          const channel = row.getValue('channel') as string;
          let iconName = 'EnvelopeIcon';
          if (channel === 'sms') iconName = 'DevicePhoneMobileIcon';
          if (channel === 'whatsapp') iconName = 'WhatsAppIcon';
          
          return (
            <div className="flex items-center gap-2">
              <Icon name={iconName as any} className="w-4 h-4" />
              {channel}
            </div>
          );
        }
      },
      {
        accessorKey: 'status',
        header: L.admin_panel.notifications.status,
        size: 100,
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          let variant: 'success' | 'error' | 'warning' = 'success';
          if (status === 'failed') variant = 'error';
          if (status === 'pending') variant = 'warning';
          
          return <Badge variant={variant}>{status}</Badge>;
        }
      }
    ];

    // Add dynamic variable columns for sent recipients
    if (recipientsData?.variables?.length) {
      // Create columns for each variable
      recipientsData?.variables?.sort().forEach(variable => {
        baseColumns.push({
          accessorFn: (row) => row.variables?.[variable],
          id: `variable_${variable}`,
          header: variable,
          size: 120,
          meta: {
            headerProps: {
              className: 'bg-green-50',
            },
            cellProps: {
              className: 'bg-green-50',
            },
          },
          cell: ({ row }) => {
            const value = row.original.variables?.[variable];
            if (value === undefined || value === null || value === '') {
              return <span className="text-gray-400 italic">--</span>;
            }
            
            // Truncate long values and show tooltip
            const displayValue = String(value);
            const truncated = displayValue.length > 20 ? displayValue.substring(0, 20) + '...' : displayValue;
            
            return displayValue.length > 20 ? (
              <Tooltip text={displayValue}>
                <span className="cursor-help">{truncated}</span>
              </Tooltip>
            ) : (
              <span>{displayValue}</span>
            );
          }
        });
      });
    }

    // Add the remaining base columns
    baseColumns.push(
      {
        accessorKey: 'sentTimestamp',
        header: L.admin_panel.notifications.sent_timestamp,
        size: 150,
        cell: ({ row }) => {
          const timestamp = row.getValue('sentTimestamp') as string;
          if (!timestamp) return '--';
          
          const date = new Date(timestamp);
          return date.toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
      },
      {
        id: 'preview',
        header: L.admin_panel.notifications.preview,
        size: 120,
        cell: ({ row }) => (
          <Button size="sm" onClick={() => openPreview('sent', row.original)} icon={<Icon name="EyeIcon" />}>
            {L.admin_panel.notifications.preview}
          </Button>
        )
      }
    );

    return baseColumns;
  };

  const createEligibleColumns = (): ColumnDef<NotificationRecipientsResult['eligible'][number]>[] => {
    const baseColumns: ColumnDef<NotificationRecipientsResult['eligible'][number]>[] = [
      {
        accessorKey: 'uid',
        header: 'UID',
        size: 120,
      },
      {
        accessorKey: 'legal_name',
        header: L.admin_panel.notifications.legal_name,
        size: 150,
      },
      {
        accessorKey: 'channel',
        header: L.admin_panel.notifications.channel,
        size: 100,
        cell: ({ row }) => {
          const channel = row.getValue('channel') as string;
          let iconName = 'EnvelopeIcon';
          if (channel === 'sms') iconName = 'DevicePhoneMobileIcon';
          if (channel === 'whatsapp') iconName = 'WhatsAppIcon';
          
          return (
            <div className="flex items-center gap-2">
              <Icon name={iconName as any} className="w-4 h-4" />
              {channel}
            </div>
          );
        }
      }
    ];

    // Add dynamic variable columns for eligible recipients
    if (recipientsData?.variables?.length) {
      // Create columns for each variable
      recipientsData?.variables.sort().forEach(variable => {
        baseColumns.push({
          accessorFn: (row) => row.variables?.[variable],
          id: `variable_${variable}`,
          header: variable,
          size: 120,
          meta: {
            headerProps: {
              className: 'bg-green-50',
            },
            cellProps: {
              className: 'bg-green-50',
            },
          },
          cell: ({ row }) => {
            const value = row.original.variables?.[variable];
            if (value === undefined || value === null || value === '') {
              return <span className="text-gray-400 italic">--</span>;
            }
            
            // Truncate long values and show tooltip
            const displayValue = String(value);
            const truncated = displayValue.length > 20 ? displayValue.substring(0, 20) + '...' : displayValue;
            
            return displayValue.length > 20 ? (
              <Tooltip text={displayValue}>
                <span className="cursor-help">{truncated}</span>
              </Tooltip>
            ) : (
              <span>{displayValue}</span>
            );
          }
        });
      });
    }

    // Add preview button at the end
    baseColumns.push({
      id: 'preview',
      header: L.admin_panel.notifications.preview,
      size: 120,
      cell: ({ row }) => (
        <Button size="sm" onClick={() => openPreview('eligible', row.original)} icon={<Icon name="EyeIcon" />}>
          {L.admin_panel.notifications.preview}
        </Button> 
      )
    });

    return baseColumns;
  };

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.notifications,
      href: '/admin-panel/notifications'
    },
    {
      label: recipientsData?.notificationName || L.admin_panel.notifications.notification_recipients
    }
  ];


  return (
    <AdminPanelPage
      title={`${recipientsData?.notificationName || L.admin_panel.notifications.notification_recipients}`}
      description={L.admin_panel.notifications.notification_recipients_desc}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
          <Button
            size="sm"
            variant='secondary'
            onClick={handleForceRefresh}
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            aria-label={L.admin_panel.payments.refresh_data}
          />
        </Tooltip>,
        <Tooltip text={activeTab === 'sent' ? L.admin_panel.notifications.export_sent_recipients : L.admin_panel.notifications.export_eligible_recipients} asChild>
          <Button 
            size="sm" 
            variant="secondary" 
            onClick={() => setDownloadModalOpen(true)}
          >
            <div className="flex gap-2 items-center">
              <Icon name="ArrowDownTrayIcon" role="decorative" type="solid" />
            </div>
          </Button>
        </Tooltip>
      ]}
    >
      <div className="flex flex-col gap-4 w-full">
        <Card>
          <CardBody>
            <Tabs
              justify="left"
              selectedTab={activeTab}
              onChange={(tab) => setActiveTab(tab as 'sent' | 'eligible')}
              tabs={[
                {
                  key: 'sent',
                  name: `${L.admin_panel.notifications.sent} (${recipientsData?.sent?.length || 0})`,
                  content: (
                    <DataTable
                      className="pt-2"
                      columns={createSentColumns()}
                      data={recipientsData?.sent || []}
                      loading={isLoading}
                      pageSize={25}
                      enableSearch
                      searchLabel={L.admin_panel.notifications.search_recipients}
                      lastUpdatedTimestamp={lastUpdatedTimestamp}
                      noDataMessage={L.admin_panel.notifications.empty_sent_notifications}
                      dataFilteredOutMessage={L.admin_panel.notifications.filtered_sent_notifications}
                    />
                  )
                },
                {
                  key: 'eligible',
                  name: `${L.admin_panel.notifications.eligible} (${recipientsData?.eligible?.length || 0})`,
                  content: (
                    <DataTable
                      className="pt-2"
                      columns={createEligibleColumns()}
                      data={recipientsData?.eligible || []}
                      loading={isLoading}
                      pageSize={25}
                      enableSearch
                      searchLabel={L.admin_panel.notifications.search_recipients}
                      lastUpdatedTimestamp={lastUpdatedTimestamp}
                      noDataMessage={L.admin_panel.notifications.empty_eligible_recipients}
                      dataFilteredOutMessage={L.admin_panel.notifications.filtered_eligible_recipients}
                    />
                  )
                }
              ]}
            />
          </CardBody>
        </Card>
      </div>
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={previewState.kind === 'sent' ? L.admin_panel.notifications.sent_message_preview : L.admin_panel.notifications.eligible_message_preview}
        size="lg"
      >
        {
          (() => {
            const recipient = previewState.recipient;
            if (!recipient) return null;
            const preview = previewData;
            return (
              <Refreshing loading={previewLoading}>
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex flex-wrap gap-2 items-center text-sm text-gray-600">
                    <Badge variant="neutral">{recipient.channel}</Badge>
                    <span>UID: {recipient.uid}</span>
                    <span>
                      {recipient.legal_name}
                    </span>
                  </div>
                  {preview?.subject && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">{L.admin_panel.notifications.subject}</div>
                      <div className="text-sm">{preview.subject}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">{L.admin_panel.notifications.body}</div>
                    <CodeDisplay code={preview?.body || ''} language="text" contentMode="wrap" expandable={false} />
                  </div>
                  {preview?.variables && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">{L.admin_panel.notifications.variables}</div>
                      <CodeDisplay code={JSON.stringify(preview.variables, null, 2)} language="json" contentMode="scroll" expandable={false} />
                    </div>
                  )}
                </div>
              </Refreshing>
            );
          })()
        }
      </Modal>
      <NotificationRecipientsDownloadModal
        notificationName={recipientsData?.notificationName || ''}
        sentData={recipientsData?.sent || []}
        eligibleData={recipientsData?.eligible || []}
        variables={recipientsData?.variables || []}
        downloadModalOpen={downloadModalOpen}
        setDownloadModalOpen={setDownloadModalOpen}
      />
    </AdminPanelPage>
  );
}; 